import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Typography,
  Space,
  message,
  Spin,
  Divider,
  Checkbox,
} from 'antd';

import { Grid } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { PrinterOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;
const EAST_PRINTER_SN = '961801376';
const WEST_PRINTER_SN = '961826173';

interface Consigner {
  id: number;
  name: string;
  phone: string;
  address?: string;
  subject_type: string[];
  default_station?: string;
}

interface SelectOption {
  label: string;
  value: number;
  displayName: string;
  isNew?: boolean;
  newName?: string;
}

interface ShippingStation {
  id: number;
  name: string;
}

interface Employee {
  id: number;
  name: string;
}

const settlementMethodOptions = [
  { label: '到付款', value: '到付款' },
  { label: '现付款', value: '现付款' },
  { label: '回付款', value: '回付款' },
  { label: '月结付', value: '月结付' },
];

const deliveryMethodOptions = [
  { label: '站点自提', value: '站点自提' },
  { label: '送货到达', value: '送货到达' },
  { label: '送货安装', value: '送货安装' },
];

const NewLogisticsOrder: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [createAndPrintPrinterSn, setCreateAndPrintPrinterSn] = useState<string | null>(null);
  const createAndPrintPrinterSnRef = useRef<string | null>(null);
  const [consigners, setConsigners] = useState<Consigner[]>([]);
  const [shippingStations, setShippingStations] = useState<ShippingStation[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  // 隐藏到付金额复选框状态
  const [hideCodAmount, setHideCodAmount] = useState(true);
  // 搜索状态
  const [shipperSearchValue, setShipperSearchValue] = useState('');
  const [receiverSearchValue, setReceiverSearchValue] = useState('');
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const navigate = useNavigate();

  // 安全的数字格式化函数
  const formatCurrency = useCallback((value: any): string => {
    const numValue = Number(value);
    return !isNaN(numValue) ? numValue.toFixed(2) : '0.00';
  }, []);
  const isDevSkipPrintApi =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_SKIP_PRINT_API === 'true';
  const getFinalCodAmount = useCallback((codTotal: any, shouldHide: boolean): string => {
    return formatCurrency(shouldHide ? 0 : codTotal);
  }, [formatCurrency]);

  // 当总件数为 0 且鼠标移入时清空显示，避免重复清空
  const totalPiecesClearedRef = useRef(false);
  const totalPieces = Form.useWatch('total_pieces', form);

  const handleTotalPiecesFocus = useCallback(() => {
    const current = form.getFieldValue('total_pieces');
    if (current === 0 && !totalPiecesClearedRef.current) {
      form.setFieldsValue({ total_pieces: undefined });
      totalPiecesClearedRef.current = true;
    }
  }, [form]);

  const handleTotalPiecesBlur = useCallback(() => {
    const current = form.getFieldValue('total_pieces');
    if (current === undefined || current === null || current === '') {
      form.setFieldsValue({ total_pieces: 0 });
    }
  }, [form]);

  // 金额字段：获取焦点时若为 0 或 0.00 则清空；失焦为空则恢复 0.00
  const moneyClearedRef = useRef<Record<string, boolean>>({});

  const handleMoneyFocus = useCallback((field: string) => {
    const current = form.getFieldValue(field);
    const isZeroLike = (v: any) => {
      if (v === 0) return true;
      if (typeof v === 'string') {
        const trimmed = v.trim();
        return trimmed === '0' || trimmed === '0.0' || trimmed === '0.00';
      }
      return false;
    };
    if (isZeroLike(current) && !moneyClearedRef.current[field]) {
      form.setFieldsValue({ [field]: null });
      moneyClearedRef.current[field] = true;
    }
  }, [form]);

  const handleMoneyBlur = useCallback((field: string) => {
    const current = form.getFieldValue(field);
    if (current === undefined || current === null || current === '') {
      form.setFieldsValue({ [field]: 0.0 });
    }
  }, [form]);

  // 值回到 0 时允许再次清空
  const freightWatch = Form.useWatch('freight', form);
  const deliveryInstallFeeWatch = Form.useWatch('delivery_install_fee', form);
  const advanceTransferFeeWatch = Form.useWatch('advance_transfer_fee', form);
  const collectionAmountWatch = Form.useWatch('collection_amount', form);
  const rebateWatch = Form.useWatch('rebate', form);
  const cashPaymentWatch = Form.useWatch('cash_payment', form);
  const deductionWatch = Form.useWatch('deduction', form);
  const discountAmountWatch = Form.useWatch('discount_amount', form);

  useEffect(() => {
    if (freightWatch === 0) moneyClearedRef.current['freight'] = false;
  }, [freightWatch]);
  useEffect(() => {
    if (deliveryInstallFeeWatch === 0) moneyClearedRef.current['delivery_install_fee'] = false;
  }, [deliveryInstallFeeWatch]);
  useEffect(() => {
    if (advanceTransferFeeWatch === 0) moneyClearedRef.current['advance_transfer_fee'] = false;
  }, [advanceTransferFeeWatch]);
  useEffect(() => {
    if (collectionAmountWatch === 0) moneyClearedRef.current['collection_amount'] = false;
  }, [collectionAmountWatch]);
  useEffect(() => {
    if (rebateWatch === 0) moneyClearedRef.current['rebate'] = false;
  }, [rebateWatch]);
  useEffect(() => {
    if (cashPaymentWatch === 0) moneyClearedRef.current['cash_payment'] = false;
  }, [cashPaymentWatch]);
  useEffect(() => {
    if (deductionWatch === 0) moneyClearedRef.current['deduction'] = false;
  }, [deductionWatch]);
  useEffect(() => {
    if (discountAmountWatch === 0) moneyClearedRef.current['discount_amount'] = false;
  }, [discountAmountWatch]);

  useEffect(() => {
    if (totalPieces === 0) {
      totalPiecesClearedRef.current = false;
    }
  }, [totalPieces]);

  // 生成物流单号
  const generateOrderNo = useCallback(() => {
    const now = dayjs();
    const year = now.format('YY');
    const month = now.format('MM');
    const day = now.format('DD');
    const hour = now.format('HH');
    const minute = now.format('mm');
    const second = now.format('ss');
    return `XY${year}${month}${day}${hour}${minute}${second}`;
  }, []);

  // 获取收发货人列表
  const fetchConsigners = useCallback(async () => {
    try {
      const response = await fetch('/api/consigners?pageSize=10000&current=1');
      if (response.ok) {
        const data = await response.json();
        setConsigners(data.data || []);
      }
    } catch (error) {
      console.error('获取收发货人列表失败:', error);
    }
  }, []);

  // 获取收发货站点列表
  const fetchShippingStations = useCallback(async () => {
    try {
      const response = await fetch('/api/shipping-stations?pageSize=1000&current=1');
      if (response.ok) {
        const data = await response.json();
        setShippingStations(data.data || []);
      }
    } catch (error) {
      console.error('获取收发货站点列表失败:', error);
    }
  }, []);

  // 获取员工列表
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await fetch('/api/employees?pageSize=1000&current=1');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.data || []);
      }
    } catch (error) {
      console.error('获取员工列表失败:', error);
    }
  }, []);

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      setDataLoading(true);
      await Promise.all([
        fetchConsigners(),
        fetchShippingStations(),
        fetchEmployees(),
      ]);
      setDataLoading(false);
    };
    initData();
  }, [fetchConsigners, fetchShippingStations, fetchEmployees]);

  // 员工列表加载后，若存在“刘金玉”且当前未选择业务归属，则默认选中
  useEffect(() => {
    if (!employees || employees.length === 0) return;
    const target = employees.find(e => e.name === '刘金玉');
    if (!target) return;
    const current = form.getFieldValue('business_owner_id');
    if (current === undefined || current === null || current === '') {
      form.setFieldsValue({ business_owner_id: target.id });
    }
  }, [employees, form]);

  // 发货人变化处理
  const handleShipperChange = useCallback((value: number) => {
    const shipper = consigners.find(c => c.id === value);
    if (shipper) {
      form.setFieldsValue({
        shipper_name: shipper.name,        // 显示名称
        shipper_phone: shipper.phone || '', // 自动填充电话
        loading_area: shipper.default_station || form.getFieldValue('loading_area'),
      });
    }
  }, [consigners, form]);

  // 收货人变化处理
  const handleReceiverChange = useCallback((value: number) => {
    const receiver = consigners.find(c => c.id === value);
    if (receiver) {
      form.setFieldsValue({
        receiver_phone: receiver.phone || '',
        receiver_address: receiver.address || '',
        // 如果有默认卸货站点，自动设置；否则保持当前值
        unloading_station: receiver.default_station || form.getFieldValue('unloading_station') || '',
      });
    }
  }, [consigners, form]);

  // 获取发货人选项
  const getShipperOptions = useCallback((inputValue: string): SelectOption[] => {
    const shipperConsigners = consigners.filter(c => 
      c.subject_type && c.subject_type.includes('发货人')
    ).sort((a, b) => a.id - b.id); // 按ID升序排序
    
    if (!inputValue) {
      return shipperConsigners.map(consigner => ({
        label: `${consigner.name} (${consigner.phone})`,
        value: consigner.id,
        displayName: `${consigner.name} (${consigner.phone})`,
      }));
    }
    
    const filtered = shipperConsigners.filter(c => 
      c.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      (c.phone && c.phone.includes(inputValue))
    );
    
    const options = filtered.map(consigner => ({
      label: `${consigner.name} (${consigner.phone})`,
      value: consigner.id,
      displayName: `${consigner.name} (${consigner.phone})`,
    }));
    
    // 检查是否有完全匹配的选项
    const hasExactMatch = filtered.some(c => c.name === inputValue);
    
    // 如果没有完全匹配且输入不为空，添加新增选项
    if (!hasExactMatch && inputValue.trim()) {
      options.push({
        label: `${inputValue.trim()}[+新增]`, // 下拉选项显示[+新增]
        value: -1, // 使用-1表示新增选项
        isNew: true,
        newName: inputValue.trim(),
        displayName: inputValue.trim() // 选择后显示的名称
      } as SelectOption);
    }

    return options;
  }, [consigners]);

  // 获取收货人选项
  const getReceiverOptions = useCallback((inputValue: string): SelectOption[] => {
    const receiverConsigners = consigners.filter(c => 
      c.subject_type && c.subject_type.includes('收货人')
    ).sort((a, b) => a.id - b.id); // 按ID升序排序
    
    if (!inputValue) {
      return receiverConsigners.map(consigner => ({
        label: `${consigner.name} (${consigner.phone})`,
        value: consigner.id,
        displayName: `${consigner.name} (${consigner.phone})`,
      }));
    }
    
    const filtered = receiverConsigners.filter(c => 
      c.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      (c.phone && c.phone.includes(inputValue))
    );
    
    const options = filtered.map(consigner => ({
      label: `${consigner.name} (${consigner.phone})`,
      value: consigner.id,
      displayName: `${consigner.name} (${consigner.phone})`,
    }));
    
    // 检查是否有完全匹配的选项
    const hasExactMatch = filtered.some(c => c.name === inputValue);
    
    // 如果没有完全匹配且输入不为空，添加新增选项
    if (!hasExactMatch && inputValue.trim()) {
      options.push({
        label: `${inputValue.trim()}[+新增]`, // 下拉选项显示[+新增]
        value: -1, // 使用-1表示新增选项
        isNew: true,
        newName: inputValue.trim(),
        displayName: inputValue.trim() // 选择后显示的名称
      } as SelectOption);
    }

    return options;
  }, [consigners]);

  // 表单值变化处理
  const handleFormValuesChange = useCallback((changedValues: any, allValues: any) => {
    // 计算总运费
    const totalFreight = (allValues.freight || 0) + 
                        (allValues.delivery_install_fee || 0) + 
                        (allValues.advance_transfer_fee || 0) + 
                        (allValues.collection_amount || 0) + 
                        (allValues.rebate || 0);
    
    // 计算应付代收款
    const payableCollection = -(allValues.collection_amount || 0);
    
    // 计算到付合计
    const codTotal = totalFreight - (allValues.cash_payment || 0) - (allValues.deduction || 0);

    form.setFieldsValue({
      total_freight: totalFreight,
      payable_collection: payableCollection,
      cod_total: codTotal,
    });
  }, [form]);

  // 创建收发货人
  const createConsigner = useCallback(async (consignerData: any) => {
    try {
      const response = await fetch('/api/consigners', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(consignerData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.code === 0) {
          return result.data.id;
        } else {
          throw new Error(result.message || '创建收发货人失败');
        }
      } else {
        throw new Error('创建收发货人失败');
      }
    } catch (error) {
      console.error('创建收发货人失败:', error);
      throw error;
    }
  }, []);

  // 提交表单
  const handleSubmit = useCallback(async (values: any) => {
    try {
      setLoading(true);
      const targetPrinterSn = createAndPrintPrinterSnRef.current || createAndPrintPrinterSn;
      setCreateAndPrintPrinterSn(null); // 重置状态
      createAndPrintPrinterSnRef.current = null;
      
      console.log('🔍 表单提交时的values:', values);
      
      let shipperId = values.shipper_id;
      let receiverId = values.receiver_id;
      
      // 处理发货人
      if (values.shipper_id === -1) {
        console.log('🔍 准备创建发货人，shipper_name:', values.shipper_name);
        // 新增发货人
        const shipperData = {
          name: values.shipper_name,
          phone: values.shipper_phone || '',
          subject_type: '发货人',
          default_station: values.loading_area || '', // 使用装货区，不是卸货站点
          status: '开启'
        };
        console.log('🔍 发货人数据:', shipperData);
        shipperId = await createConsigner(shipperData);
        console.log('创建发货人成功，ID:', shipperId);
      }
      
      // 处理收货人
      if (values.receiver_id === -1) {
        console.log('🔍 准备创建收货人，receiver_name:', values.receiver_name);
        // 新增收货人
        const receiverData = {
          name: values.receiver_name,
          phone: values.receiver_phone || '',
          address: values.receiver_address || '',
          subject_type: '收货人',
          default_station: values.unloading_station || '',
          status: '开启'
        };
        console.log('🔍 收货人数据:', receiverData);
        receiverId = await createConsigner(receiverData);
        console.log('创建收货人成功，ID:', receiverId);
      }
      
      // 根据ID获取发货人和收货人的姓名
      const shipper = consigners.find(c => c.id === shipperId);
      const receiver = consigners.find(c => c.id === receiverId);
      const businessOwner = employees.find(e => e.id === values.business_owner_id);
      
      const orderData = {
        ...values,
        shipper_id: shipperId,
        receiver_id: receiverId,
        receive_date: values.receive_date?.format('YYYY-MM-DD HH:mm:ss'),
        // 添加姓名字段
        shipper_name: shipper?.name || values.shipper_name || '',
        receiver_name: receiver?.name || values.receiver_name || '',
        business_owner_name: businessOwner?.name || '',
      };

      const response = await fetch('/api/logistics-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('创建物流单API响应:', result);
        
        // 检查后端返回的数据结构
        if (result.code === 0 || result.success === true) {
          messageApi.success({
            content: '物流单创建成功！',
            duration: 3,
          });
          
          // 如果需要打印，调用打印功能
          if (targetPrinterSn) {
            try {
              // 使用完整的订单数据，包含创建后的ID
              const orderDataWithId = {
                ...orderData,
                id: result.data?.id || result.id,
              };
              await handlePrintAfterCreate(orderDataWithId, targetPrinterSn);
            } catch (printError) {
              console.error('打印失败:', printError);
              messageApi.warning('创建成功，但打印失败');
            }
          }
          
          form.resetFields();
          // 重新生成物流单号
          form.setFieldsValue({
            order_no: generateOrderNo(),
            receive_date: dayjs(),
          });
          // 延迟跳转，让用户看到成功消息
          setTimeout(() => {
            navigate('/logistics/orders'); // 跳转到物流单列表页面
          }, 2000);
        } else {
          messageApi.error({
            content: result.message || '创建失败',
            duration: 3,
          });
        }
      } else {
        messageApi.error({
          content: '创建失败',
          duration: 3,
        });
      }
    } catch (error) {
      console.error('提交失败:', error);
      messageApi.error({
        content: '提交失败',
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  }, [consigners, employees, form, generateOrderNo, messageApi, navigate, createAndPrintPrinterSn, createConsigner]);

  // 创建后打印功能
  const handlePrintAfterCreate = useCallback(async (orderData: any, targetPrinterSn: string) => {
    console.log('🖨️ 开始创建后打印流程');
    console.log('📦 订单数据:', orderData);
    console.log('🎯 目标打印机编号:', targetPrinterSn);
    
    try {
      // 获取云打印机设备
      const response = await fetch('/api/devices?device_category=云打印机');
      
      if (response.ok) {
        const data = await response.json();
        console.log('📡 设备列表响应:', data);
        
        if (data.success && data.data.length > 0) {
          const availableDevices = data.data.filter((device: any) => device.status === '已绑定');
          console.log('🔒 可用设备:', availableDevices);
          
          if (availableDevices.length > 0) {
            const device = availableDevices.find((item: any) => item.device_sn === targetPrinterSn);
            if (!device) {
              console.log('❌ 没有找到指定编号的已绑定打印设备:', targetPrinterSn);
              messageApi.warning(`未找到编号 ${targetPrinterSn} 的已绑定云打印机`);
              return;
            }
            console.log('🎯 使用设备:', device);
            
            // 获取系统设置
            const settingsResponse = await fetch('/api/settings');
            const settingsData = await settingsResponse.json();
            const systemSettings = settingsData.success ? settingsData.data : null;
            
            // 使用与云打印相同的前端替换逻辑
            const template = device.template || `<CB><B>{0}</B></CB><BR>
物流单号:{1}<BR>
日期:{2}<BR>
---------------------------------<BR>
收货站点：<B>{3}</B><BR>
收货人：<B>{4}</B><BR>
收货电话：<B>{5}</B><BR>
地址：<B>{6}</B><BR>
---------------------------------<BR>
托运人：{7}<BR>
厂家电话：{8}<BR>
---------------------------------<BR>
货品名称：{9}<BR>
送货方式：{10}<BR>
件数：<B>{11}</B><BR>
按计：<BR>
代收货款：{12}<BR>
现付款：{13}<BR>
垫付款：{14}<BR>
到付金额：{15}<BR>
备注：{16}<BR>
{17}`;
            
            // 前端替换占位符
            let printContent = template;
            const replacements: Record<string, string> = {
              '{0}': systemSettings?.company_short_name || '兴运物流',
              '{1}': orderData.order_no || '',
              '{2}': orderData.receive_date || '',
              '{3}': orderData.unloading_station || '',
              '{4}': orderData.receiver_name || '',
              '{5}': orderData.receiver_phone || '',
              '{6}': orderData.receiver_address || '',
              '{7}': orderData.shipper_name || '',
              '{8}': orderData.shipper_phone || '',
              '{9}': orderData.goods_name || '',
              '{10}': orderData.delivery_method || '',
              '{11}': String(orderData.total_pieces || ''),
              '{12}': formatCurrency(orderData.collection_amount),
              '{13}': formatCurrency(orderData.cash_payment),
              '{14}': formatCurrency(orderData.advance_transfer_fee),
              '{15}': (() => {
                const finalValue = getFinalCodAmount(orderData.cod_total, hideCodAmount);
                console.log(
                  `🔍 新增页面到付金额计算: hideCodAmount=${hideCodAmount}, cod_total=${orderData.cod_total}, finalValue=${finalValue}`
                );
                return finalValue;
              })(),
              '{16}': String(orderData.receive_remark || ''),
              '{17}': systemSettings?.company_remark || ''
            };

            // 执行替换
            Object.keys(replacements).forEach(key => {
              printContent = printContent.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacements[key]);
            });

            if (isDevSkipPrintApi) {
              console.log('🧪 调试模式：跳过云打印接口调用', {
                order_no: orderData.order_no,
                hideCodAmount,
                cod_total: orderData.cod_total,
                finalCodAmount: replacements['{15}']
              });
              messageApi.info('调试模式：已跳过打印接口调用，请查看控制台到付金额日志');
              return;
            }

            // 调用打印API
            const printResponse = await fetch('/api/devices/print-order', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                orderData: orderData,
                deviceSn: device.sn || device.device_sn || device.deviceSn,
                template: printContent,
                times: 1
              }),
            });
            
            if (printResponse.ok) {
              const printResult = await printResponse.json();
              console.log('✅ 打印成功:', printResult);
              messageApi.success('打印成功！');
            } else {
              console.error('❌ 打印失败:', printResponse.status);
              messageApi.error('打印失败');
            }
          } else {
            console.log('❌ 没有可用的打印设备');
            messageApi.warning('没有可用的打印设备');
          }
        } else {
          console.log('❌ 没有找到打印设备');
          messageApi.warning('没有找到打印设备');
        }
      } else {
        console.error('❌ 获取设备列表失败:', response.status);
        messageApi.error('获取设备列表失败');
      }
    } catch (error) {
      console.error('❌ 打印过程中出错:', error);
      messageApi.error('打印过程中出错');
    }
  }, [messageApi, getFinalCodAmount, hideCodAmount, isDevSkipPrintApi]);

  // 设置默认值
  useEffect(() => {
    if (!dataLoading) {
      form.setFieldsValue({
        order_no: generateOrderNo(),
        receive_date: dayjs(),
        loading_area: shippingStations.find(s => s.name === '香河')?.name || '',
        total_pieces: 0,
        freight: 0.00,
        delivery_install_fee: 0.00,
        advance_transfer_fee: 0.00,
        collection_amount: 0.00,
        rebate: 0.00,
        total_freight: 0.00,
        cash_payment: 0.00,
        deduction: 0.00,
        payable_collection: 0.00,
        settlement_method: '到付款',
        cod_total: 0.00,
        discount_amount: 0.00,
        delivery_method: '站点自提',
      });
    }
  }, [dataLoading, form, generateOrderNo, shippingStations]);

  if (dataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>加载中...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0px' }}>
      {contextHolder}
      <Card style={{ padding: '0px' }}>
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormValuesChange}
          initialValues={{
            order_no: generateOrderNo(),
            receive_date: dayjs(),
            loading_area: shippingStations.find(s => s.name === '香河')?.name || '',
            total_pieces: 0,
            freight: 0.00,
            delivery_install_fee: 0.00,
            advance_transfer_fee: 0.00,
            collection_amount: 0.00,
            rebate: 0.00,
            total_freight: 0.00,
            cash_payment: 0.00,
            deduction: 0.00,
            payable_collection: 0.00,
            settlement_method: '到付款',
            cod_total: 0.00,
            discount_amount: 0.00,
            delivery_method: '站点自提',
          }}
        >
          {/* 收货信息 */}
          <Title level={5} style={{ marginTop: '0px', marginBottom: '8px' }}>
            收货信息
          </Title>
          
          {isMobile ? (
            // 移动端单列布局
            <>
              {/* 第一行：收货人、收货人电话、收货人地址、卸货站点 */}
              <Form.Item
                name="receiver_id"
                label="收货人"
                rules={[{ required: true, message: '请选择收货人' }]}
              >
                <Select
                  placeholder="请选择收货人"
                  showSearch
                  filterOption={false}
                  optionLabelProp="displayName"
                  onSearch={(value) => {
                    // 搜索时更新选项
                    setReceiverSearchValue(value);
                  }}
                  onChange={(value, option) => {
                    const selectedOption = Array.isArray(option) ? option[0] : option;
                    if (selectedOption?.isNew) {
                      // 如果是新增选项，填入姓名（不包含[+新增]标识符）
                      form.setFieldsValue({
                        receiver_id: -1, // 设置为-1表示新增
                        receiver_name: selectedOption.newName,
                        receiver_phone: '',
                        receiver_address: '',
                      });
                    } else {
                      handleReceiverChange(value);
                    }
                  }}
                  options={getReceiverOptions(receiverSearchValue)}
                />
              </Form.Item>

              <Form.Item
                name="receiver_phone"
                label="收货人电话"
              >
                <Input placeholder="收货人电话" />
              </Form.Item>

              {/* 隐藏的收货人姓名字段 */}
              <Form.Item name="receiver_name" style={{ display: 'none' }}>
                <Input />
              </Form.Item>

              <Form.Item
                name="receiver_address"
                label="收货人地址"
              >
                <Input placeholder="收货人地址" />
              </Form.Item>

              <Form.Item
                name="unloading_station"
                label="卸货站点"
                rules={[{ required: true, message: '请选择卸货站点' }]}
              >
                <Select
                  placeholder="请选择卸货站点"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {shippingStations.map(station => (
                    <Select.Option key={station.id} value={station.name}>
                      {station.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {/* 第二行：发货人、发货人电话、装货区、收货日期、物流单号 */}
              <Form.Item
                name="shipper_id"
                label="发货人"
                rules={[{ required: true, message: '请选择发货人' }]}
              >
                <Select
                  placeholder="请选择发货人"
                  showSearch
                  filterOption={false}
                  optionLabelProp="displayName"
                  onSearch={(value) => {
                    // 搜索时更新选项
                    setShipperSearchValue(value);
                  }}
                  onChange={(value, option) => {
                    const selectedOption = Array.isArray(option) ? option[0] : option;
                    console.log('🔍 PC模式发货人选择时的selectedOption:', selectedOption);
                    if (selectedOption?.isNew) {
                      // 如果是新增选项，填入姓名（不包含[+新增]标识符）
                      console.log('🔍 PC模式新增发货人，newName:', selectedOption.newName);
                      form.setFieldsValue({
                        shipper_id: -1, // 设置为-1表示新增
                        shipper_name: selectedOption.newName,
                        shipper_phone: '',
                      });
                    } else {
                      handleShipperChange(value);
                    }
                  }}
                  options={getShipperOptions(shipperSearchValue)}
                />
              </Form.Item>

              <Form.Item
                name="shipper_phone"
                label="发货人电话"
              >
                <Input placeholder="发货人电话" />
              </Form.Item>

              {/* 隐藏的发货人姓名字段 */}
              <Form.Item name="shipper_name" style={{ display: 'none' }}>
                <Input />
              </Form.Item>

              <Form.Item
                name="loading_area"
                label="装货区"
                rules={[{ required: true, message: '请选择装货区' }]}
              >
                <Select
                  placeholder="请选择装货区"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {shippingStations.map(station => (
                    <Select.Option key={station.id} value={station.name}>
                      {station.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="receive_date"
                label="收货日期"
                rules={[{ required: true, message: '请选择收货日期' }]}
              >
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: '100%' }}
                  placeholder="请选择收货日期"
                />
              </Form.Item>

              <Form.Item
                name="order_no"
                label="物流单号"
                rules={[{ required: true, message: '请输入物流单号' }]}
              >
                <Input disabled placeholder="物流单号" />
              </Form.Item>
            </>
          ) : (
            // PC端五列布局
            <>
              {/* 第一行：收货人、收货人电话、收货人地址、卸货站点 */}
              <Row gutter={16}>
                <Col span={5}>
                  <Form.Item
                    name="receiver_id"
                    label="收货人"
                    rules={[{ required: true, message: '请选择收货人' }]}
                  >
                    <Select
                      placeholder="请选择收货人"
                      showSearch
                      filterOption={false}
                      optionLabelProp="displayName"
                      onSearch={(value) => {
                        // 搜索时更新选项
                        setReceiverSearchValue(value);
                      }}
                      onChange={(value, option) => {
                        const selectedOption = Array.isArray(option) ? option[0] : option;
                        if (selectedOption?.isNew) {
                          // 如果是新增选项，填入姓名（不包含[+新增]标识符）
                          form.setFieldsValue({
                            receiver_name: selectedOption.newName,
                            receiver_phone: '',
                            receiver_address: '',
                          });
                        } else {
                          handleReceiverChange(value);
                        }
                      }}
                      options={getReceiverOptions(receiverSearchValue)}
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="receiver_phone"
                    label="收货人电话"
                  >
                    <Input placeholder="收货人电话" />
                  </Form.Item>

                  {/* 隐藏的收货人姓名字段 */}
                  <Form.Item name="receiver_name" style={{ display: 'none' }}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item
                    name="receiver_address"
                    label="收货人地址"
                  >
                    <Input placeholder="收货人地址" />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="unloading_station"
                    label="卸货站点"
                    rules={[{ required: true, message: '请选择卸货站点' }]}
                  >
                    <Select
                      placeholder="请选择卸货站点"
                      showSearch
                      filterOption={(input, option) =>
                        String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {shippingStations.map(station => (
                        <Select.Option key={station.id} value={station.name}>
                          {station.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* 第二行：发货人、发货人电话、装货区、收货日期、物流单号 */}
              <Row gutter={16}>
                <Col span={5}>
                  <Form.Item
                    name="shipper_id"
                    label="发货人"
                    rules={[{ required: true, message: '请选择发货人' }]}
                  >
                    <Select
                      placeholder="请选择发货人"
                      showSearch
                      filterOption={false}
                      optionLabelProp="displayName"
                      onSearch={(value) => {
                        // 搜索时更新选项
                        setShipperSearchValue(value);
                      }}
                      onChange={(value, option) => {
                        const selectedOption = Array.isArray(option) ? option[0] : option;
                        console.log('🔍 发货人选择时的selectedOption:', selectedOption);
                        if (selectedOption?.isNew) {
                          // 如果是新增选项，填入姓名（不包含[+新增]标识符）
                          console.log('🔍 新增发货人，newName:', selectedOption.newName);
                          form.setFieldsValue({
                            shipper_id: -1, // 设置为-1表示新增
                            shipper_name: selectedOption.newName,
                            shipper_phone: '',
                          });
                        } else {
                          handleShipperChange(value);
                        }
                      }}
                      options={getShipperOptions(shipperSearchValue)}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    name="shipper_phone"
                    label="发货人电话"
                  >
                    <Input placeholder="发货人电话" />
                  </Form.Item>

                  {/* 隐藏的发货人姓名字段 */}
                  <Form.Item name="shipper_name" style={{ display: 'none' }}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="loading_area"
                    label="装货区"
                    rules={[{ required: true, message: '请选择装货区' }]}
                  >
                    <Select
                      placeholder="请选择装货区"
                      showSearch
                      filterOption={(input, option) =>
                        String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {shippingStations.map(station => (
                        <Select.Option key={station.id} value={station.name}>
                          {station.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="receive_date"
                    label="收货日期"
                    rules={[{ required: true, message: '请选择收货日期' }]}
                  >
                    <DatePicker
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      style={{ width: '100%' }}
                      placeholder="请选择收货日期"
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="order_no"
                    label="物流单号"
                    rules={[{ required: true, message: '请输入物流单号' }]}
                  >
                    <Input disabled placeholder="物流单号" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* 货品信息 */}
          <Title level={5} style={{ marginTop: '8px', marginBottom: '8px' }}>
            货品信息
          </Title>
          
          {isMobile ? (
            // 移动端单列布局
            <>
              <Form.Item
                name="goods_name"
                label="货物名称"
                rules={[{ required: true, message: '请输入货物名称' }]}
              >
                <Input placeholder="货物名称" />
              </Form.Item>

              <Form.Item
                name="total_pieces"
                label="总件数"
                rules={[{ required: true, message: '请输入总件数' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="总件数"
                  onFocus={handleTotalPiecesFocus}
                  onBlur={handleTotalPiecesBlur}
                />
              </Form.Item>

              <Form.Item
                name="freight"
                label="运费"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  prefix="¥"
                  onFocus={() => handleMoneyFocus('freight')}
                  onClick={() => handleMoneyFocus('freight')}
                  onBlur={() => handleMoneyBlur('freight')}
                />
              </Form.Item>

              <Form.Item
                name="delivery_install_fee"
                label="送货安装费"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  prefix="¥"
                  onFocus={() => handleMoneyFocus('delivery_install_fee')}
                  onClick={() => handleMoneyFocus('delivery_install_fee')}
                  onBlur={() => handleMoneyBlur('delivery_install_fee')}
                />
              </Form.Item>

              <Form.Item
                name="advance_transfer_fee"
                label="垫付中转费"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  prefix="¥"
                  onFocus={() => handleMoneyFocus('advance_transfer_fee')}
                  onClick={() => handleMoneyFocus('advance_transfer_fee')}
                  onBlur={() => handleMoneyBlur('advance_transfer_fee')}
                />
              </Form.Item>

              <Form.Item
                name="collection_amount"
                label="代收货款"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  prefix="¥"
                  onFocus={() => handleMoneyFocus('collection_amount')}
                  onClick={() => handleMoneyFocus('collection_amount')}
                  onBlur={() => handleMoneyBlur('collection_amount')}
                />
              </Form.Item>

              <Form.Item
                name="rebate"
                label="返点"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  prefix="¥"
                  onFocus={() => handleMoneyFocus('rebate')}
                  onClick={() => handleMoneyFocus('rebate')}
                  onBlur={() => handleMoneyBlur('rebate')}
                />
              </Form.Item>
            </>
          ) : (
            // PC端五列布局
            <>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item
                    name="goods_name"
                    label="货物名称"
                    rules={[{ required: true, message: '请输入货物名称' }]}
                  >
                    <Input placeholder="货物名称" />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="total_pieces"
                    label="总件数"
                    rules={[{ required: true, message: '请输入总件数' }]}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: '100%' }}
                      placeholder="总件数"
                      onFocus={handleTotalPiecesFocus}
                      onBlur={handleTotalPiecesBlur}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="freight"
                    label="运费"
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      prefix="¥"
                      onFocus={() => handleMoneyFocus('freight')}
                      onClick={() => handleMoneyFocus('freight')}
                      onBlur={() => handleMoneyBlur('freight')}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="delivery_install_fee"
                    label="送货安装费"
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      prefix="¥"
                      onFocus={() => handleMoneyFocus('delivery_install_fee')}
                      onClick={() => handleMoneyFocus('delivery_install_fee')}
                      onBlur={() => handleMoneyBlur('delivery_install_fee')}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="advance_transfer_fee"
                    label="垫付中转费"
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      prefix="¥"
                      onFocus={() => handleMoneyFocus('advance_transfer_fee')}
                      onClick={() => handleMoneyFocus('advance_transfer_fee')}
                      onBlur={() => handleMoneyBlur('advance_transfer_fee')}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="collection_amount"
                    label="代收货款"
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      prefix="¥"
                      onFocus={() => handleMoneyFocus('collection_amount')}
                      onClick={() => handleMoneyFocus('collection_amount')}
                      onBlur={() => handleMoneyBlur('collection_amount')}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="rebate"
                    label="返点"
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      prefix="¥"
                      onFocus={() => handleMoneyFocus('rebate')}
                      onClick={() => handleMoneyFocus('rebate')}
                      onBlur={() => handleMoneyBlur('rebate')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* 运费支付 */}
          <Title level={5} style={{ marginTop: '8px', marginBottom: '8px' }}>
            运费支付
          </Title>
          
          {isMobile ? (
            // 移动端单列布局
            <>
              <Form.Item
                name="total_freight"
                label="总运费"
              >
                <InputNumber
                  disabled
                  style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                  placeholder="0.00"
                  prefix="¥"
                />
              </Form.Item>

              <Form.Item
                name="cash_payment"
                label="现付款"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  prefix="¥"
                  onFocus={() => handleMoneyFocus('cash_payment')}
                  onClick={() => handleMoneyFocus('cash_payment')}
                  onBlur={() => handleMoneyBlur('cash_payment')}
                />
              </Form.Item>

              <Form.Item
                name="deduction"
                label="代扣"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  prefix="¥"
                  onFocus={() => handleMoneyFocus('deduction')}
                  onClick={() => handleMoneyFocus('deduction')}
                  onBlur={() => handleMoneyBlur('deduction')}
                />
              </Form.Item>

              <Form.Item
                name="payable_collection"
                label="应付代收款"
              >
                <InputNumber
                  disabled
                  style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                  placeholder="0.00"
                  prefix="¥"
                />
              </Form.Item>

              <Form.Item
                name="cod_total"
                label="到付金额"
              >
                <InputNumber
                  disabled
                  style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                  placeholder="0.00"
                  prefix="¥"
                />
              </Form.Item>

              <Form.Item
                name="settlement_method"
                label="结算方式"
              >
                <Select placeholder="请选择结算方式">
                  {settlementMethodOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="discount_amount"
                label="优惠金额"
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  prefix="¥"
                  onFocus={() => handleMoneyFocus('discount_amount')}
                  onClick={() => handleMoneyFocus('discount_amount')}
                  onBlur={() => handleMoneyBlur('discount_amount')}
                />
              </Form.Item>

              <Form.Item
                name="delivery_method"
                label="送货方式"
              >
                <Select placeholder="请选择送货方式">
                  {deliveryMethodOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="business_owner_id"
                label="业务归属"
                rules={[{ required: true, message: '请选择业务归属' }]}
              >
                <Select
                  placeholder="请选择业务归属"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {employees.map(employee => (
                    <Select.Option key={employee.id} value={employee.id}>
                      {employee.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          ) : (
            // PC端五列布局
            <>
              <Row gutter={16}>
                <Col span={2}>
                  <Form.Item
                    name="total_freight"
                    label="总运费"
                  >
                    <InputNumber
                      disabled
                      style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                      placeholder="0.00"
                      prefix="¥"
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item
                    name="cash_payment"
                    label="现付款"
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      prefix="¥"
                      onFocus={() => handleMoneyFocus('cash_payment')}
                      onClick={() => handleMoneyFocus('cash_payment')}
                      onBlur={() => handleMoneyBlur('cash_payment')}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item
                    name="deduction"
                    label="代扣"
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      prefix="¥"
                      onFocus={() => handleMoneyFocus('deduction')}
                      onClick={() => handleMoneyFocus('deduction')}
                      onBlur={() => handleMoneyBlur('deduction')}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item
                    name="payable_collection"
                    label="应付代收款"
                  >
                    <InputNumber
                      disabled
                      style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                      placeholder="0.00"
                      prefix="¥"
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item
                    name="cod_total"
                    label="到付金额"
                  >
                    <InputNumber
                      disabled
                      style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                      placeholder="0.00"
                      prefix="¥"
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="settlement_method"
                    label="结算方式"
                  >
                    <Select placeholder="请选择结算方式">
                      {settlementMethodOptions.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item
                    name="discount_amount"
                    label="优惠金额"
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0.00"
                      prefix="¥"
                      onFocus={() => handleMoneyFocus('discount_amount')}
                      onClick={() => handleMoneyFocus('discount_amount')}
                      onBlur={() => handleMoneyBlur('discount_amount')}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    name="delivery_method"
                    label="送货方式"
                  >
                    <Select placeholder="请选择送货方式">
                      {deliveryMethodOptions.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="business_owner_id"
                    label="业务归属"
                    rules={[{ required: true, message: '请选择业务归属' }]}
                  >
                    <Select
                      placeholder="请选择业务归属"
                      showSearch
                      filterOption={(input, option) =>
                        String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {employees.map(employee => (
                        <Select.Option key={employee.id} value={employee.id}>
                          {employee.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* 收货备注 */}
          <Form.Item
            name="receive_remark"
            label="收货备注"
            style={{ marginTop: '16px' }}
          >
            <TextArea
              rows={4}
              placeholder="请输入收货备注"
              maxLength={500}
              showCount
            />
          </Form.Item>

          {/* 表单操作按钮 */}
          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Checkbox 
                checked={hideCodAmount}
                onChange={(e) => setHideCodAmount(e.target.checked)}
                style={{ marginRight: 8 }}
              >
                隐藏到付金额
              </Checkbox>
              <Button 
                icon={<PrinterOutlined />}
                onClick={() => {
                  createAndPrintPrinterSnRef.current = EAST_PRINTER_SN;
                  setCreateAndPrintPrinterSn(EAST_PRINTER_SN);
                  form.submit();
                }}
                loading={loading}
                style={{ 
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff',
                  color: 'white'
                }}
              >
                创建并打印（东）
              </Button>
              <Button 
                icon={<PrinterOutlined />}
                onClick={() => {
                  createAndPrintPrinterSnRef.current = WEST_PRINTER_SN;
                  setCreateAndPrintPrinterSn(WEST_PRINTER_SN);
                  form.submit();
                }}
                loading={loading}
                style={{ 
                  backgroundColor: '#1677ff',
                  borderColor: '#1677ff',
                  color: 'white'
                }}
              >
                创建并打印（西）
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                创建
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default NewLogisticsOrder;
